// Tekent de marktdag (een afgesloten straat) en de minikaart.
// Methoden worden op BezorgerGame.prototype gezet (zie game/mixins.js).
import { urgencyColor } from './targets.js';
import { t } from '../i18n';

const STALL_COLORS = ['#dc2626', '#16a34a', '#2563eb', '#d97706'];

export class RenderMarketMixin {
  /** Marktkramen op een afgesloten straat, met hekken aan beide kanten en een geen-toegang-bord ervoor. */
  renderClosures(ctx) {
    for (const cl of this.map.closures ?? []) {
      if (!this.inView(cl.x + cl.w / 2, cl.y + cl.h / 2, Math.max(cl.w, cl.h) + 120)) continue;
      const horizontal = cl.axis !== 'v';
      ctx.save();

      // Plein van lichte klinkers
      ctx.fillStyle = this.isNight ? '#6b6258' : '#d9c7a4';
      ctx.fillRect(cl.x, cl.y, cl.w, cl.h);
      ctx.strokeStyle = 'rgba(120, 90, 50, 0.22)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = cl.x; x <= cl.x + cl.w; x += 14) {
        ctx.moveTo(x, cl.y);
        ctx.lineTo(x, cl.y + cl.h);
      }
      for (let y = cl.y; y <= cl.y + cl.h; y += 14) {
        ctx.moveTo(cl.x, y);
        ctx.lineTo(cl.x + cl.w, y);
      }
      ctx.stroke();

      // Twee rijen kramen langs de lange kant, met een looppad ertussen
      const length = horizontal ? cl.w : cl.h;
      const depth = horizontal ? cl.h : cl.w;
      const stallLen = 40;
      const stallDepth = Math.min(34, depth / 2 - 10);
      const count = Math.max(2, Math.floor((length - 24) / (stallLen + 8)));
      const gap = (length - count * stallLen) / (count + 1);
      for (let row = 0; row < 2; row++) {
        for (let i = 0; i < count; i++) {
          const along = gap + i * (stallLen + gap);
          const across = row === 0 ? 8 : depth - 8 - stallDepth;
          const x = horizontal ? cl.x + along : cl.x + across;
          const y = horizontal ? cl.y + across : cl.y + along;
          const w = horizontal ? stallLen : stallDepth;
          const h = horizontal ? stallDepth : stallLen;
          const color = STALL_COLORS[(i + row * 2) % STALL_COLORS.length];
          // Kraam: tafel met streepjesluifel
          ctx.fillStyle = '#78350f';
          ctx.fillRect(x, y, w, h);
          ctx.fillStyle = color;
          const stripes = 5;
          for (let s = 0; s < stripes; s++) {
            if (s % 2) continue;
            if (horizontal) ctx.fillRect(x + (s * w) / stripes, y, w / stripes, h);
            else ctx.fillRect(x, y + (s * h) / stripes, w, h / stripes);
          }
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          for (let s = 1; s < stripes; s += 2) {
            if (horizontal) ctx.fillRect(x + (s * w) / stripes, y, w / stripes, h);
            else ctx.fillRect(x, y + (s * h) / stripes, w, h / stripes);
          }
          ctx.strokeStyle = 'rgba(0,0,0,0.35)';
          ctx.lineWidth = 1;
          ctx.strokeRect(x, y, w, h);
        }
      }

      // Hekken aan de korte kanten (rood-wit)
      const plank = (px, py, pw, ph) => {
        const n = 6;
        for (let i = 0; i < n; i++) {
          ctx.fillStyle = i % 2 ? '#ffffff' : '#dc2626';
          if (horizontal) ctx.fillRect(px, py + (i * ph) / n, pw, ph / n);
          else ctx.fillRect(px + (i * pw) / n, py, pw / n, ph);
        }
      };
      if (horizontal) {
        plank(cl.x - 7, cl.y, 7, cl.h);
        plank(cl.x + cl.w, cl.y, 7, cl.h);
      } else {
        plank(cl.x, cl.y - 7, cl.w, 7);
        plank(cl.x, cl.y + cl.h, cl.w, 7);
      }

      // Geen-toegang-borden vóór beide hekken
      const sign = (sx, sy) => {
        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.arc(sx, sy, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(sx - 7, sy - 2.5, 14, 5);
      };
      if (horizontal) {
        sign(cl.x - 56, cl.y + cl.h / 2);
        sign(cl.x + cl.w + 56, cl.y + cl.h / 2);
      } else {
        sign(cl.x + cl.w / 2, cl.y - 56);
        sign(cl.x + cl.w / 2, cl.y + cl.h + 56);
      }

      // Naam
      ctx.font = '800 13px "Plus Jakarta Sans Variable", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.8)';
      ctx.fillStyle = '#ffffff';
      ctx.strokeText('🛒 MARKT', cl.x + cl.w / 2, cl.y + cl.h / 2);
      ctx.fillText(t('world.market'), cl.x + cl.w / 2, cl.y + cl.h / 2);
      ctx.restore();
    }
  }

  /** Eén keer de hele stad op klein formaat tekenen (wegen, gracht, gebouwen); daarna alleen nog kopiëren. */
  buildMinimapBase(size) {
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const g = c.getContext('2d');
    const k = size / this.map.width;
    g.fillStyle = '#6a9e3a';
    g.fillRect(0, 0, size, size);
    for (const canal of this.map.canals ?? []) {
      g.fillStyle = '#0e7490';
      g.fillRect(canal.x * k, canal.y * k, Math.max(2, canal.w * k), canal.h * k);
    }
    g.fillStyle = '#44403c';
    for (const r of this.map.roads) g.fillRect(r.x * k, r.y * k, Math.max(2, r.w * k), Math.max(2, r.h * k));
    g.fillStyle = '#e7e5e4';
    for (const b of this.map.buildings) g.fillRect(b.x * k, b.y * k, Math.max(1.5, b.w * k), Math.max(1.5, b.h * k));
    // De brug: een lichte streep over de gracht
    const br = this.map.drawBridges?.[0];
    if (br) {
      g.fillStyle = '#facc15';
      g.fillRect(br.deckX * k - 1, br.deckY * k + 1, br.deckW * k + 2, Math.max(2, br.deckH * k - 2));
    }
    return c;
  }

  /** De minikaart in een hoek: de stad, afsluitingen, doelen, Sjors en jij. In schermcoördinaten. */
  renderMinimap(ctx, w, h) {
    if (!this.save.settings.minimap) return;
    if (this.mode !== 'shift' && this.mode !== 'freeride') return;
    const size = Math.round(Math.max(92, Math.min(132, Math.min(w, h) * 0.26)));
    if (!this.minimapBase || this.minimapBase.width !== 200) this.minimapBase = this.buildMinimapBase(200);
    const k = size / this.map.width;
    const portrait = h > w;
    // Rechtop rechts onder de aanbiedingen; liggend in het midden bovenaan, tussen de kolommen met bestellingen.
    const x = portrait ? w - size - 10 : Math.round((w - size) / 2);
    const y = Math.round(this.safeTop ?? 70);
    if (y + size > h - (this.safeBottom ?? 24)) return; // geen ruimte: liever geen kaart dan een kaart over de knoppen

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, 14);
    ctx.clip();
    ctx.globalAlpha = 0.93;
    ctx.drawImage(this.minimapBase, x, y, size, size);
    if (this.isNight) {
      ctx.fillStyle = 'rgba(10, 15, 28, 0.38)';
      ctx.fillRect(x, y, size, size);
    }
    ctx.globalAlpha = 1;

    // Afsluitingen
    for (const cl of this.map.closures ?? []) {
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(x + cl.x * k - 1, y + cl.y * k - 1, Math.max(5, cl.w * k) + 2, Math.max(5, cl.h * k) + 2);
    }
    // Brug open: rood streepje
    if (this.bridgeOpen?.()) {
      const br = this.map.drawBridges?.[0];
      if (br) {
        ctx.fillStyle = '#ef4444';
        ctx.fillRect(x + br.deckX * k - 1, y + br.deckY * k + 1, br.deckW * k + 2, Math.max(2, br.deckH * k - 2));
      }
    }
    // Bakkerij
    ctx.fillStyle = '#f59e0b';
    ctx.beginPath();
    ctx.arc(x + 1270 * k, y + 1055 * k, 3.4, 0, Math.PI * 2);
    ctx.fill();
    // Doelen
    for (const t of this.targets ?? []) {
      const active = t === this.activeTarget;
      ctx.fillStyle = urgencyColor(t, this.save.settings.colorblind);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = active ? 1.8 : 1;
      ctx.beginPath();
      ctx.arc(x + t.x * k, y + t.y * k, active ? 4.6 : 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    // Sjors tijdens een race
    const van = this.map.van;
    if (van?.race) {
      ctx.fillStyle = '#fb923c';
      ctx.strokeStyle = '#7c2d12';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(x + van.x * k, y + van.y * k, 3.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    // Het stuk dat je op het scherm ziet
    if (this.view) {
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = 1;
      ctx.strokeRect(x + this.view.x0 * k, y + this.view.y0 * k, (this.view.x1 - this.view.x0) * k, (this.view.y1 - this.view.y0) * k);
    }
    // Jij: een pijl in de rijrichting
    const s = this.scooter;
    ctx.save();
    ctx.translate(x + s.x * k, y + s.y * k);
    ctx.rotate(s.angle);
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(6, 0);
    ctx.lineTo(-4, -4);
    ctx.lineTo(-2, 0);
    ctx.lineTo(-4, 4);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.restore();

    // Rand
    ctx.save();
    ctx.strokeStyle = 'rgba(15, 23, 42, 0.75)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, 14);
    ctx.stroke();
    ctx.restore();
  }
}
